import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CreditCard,
  GitBranch,
  Inbox,
  Layers,
  Lock,
  MapPin,
  MinusSquare,
  Package,
  Plus,
  RefreshCw,
  Scissors,
  Search,
  Slash,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/Badge";
import { BinPickerModal } from "@/components/BinPickerModal";
import { EmptyState } from "@/components/EmptyState";
import { FilterSelect, type FilterOption } from "@/components/FilterSelect";
import { Modal } from "@/components/Modal";
import {
  useApp,
  type Card,
  type CardCondition,
  type CardPatch,
  type PriceSource,
} from "@/context/AppContext";
import { conditionAdjust, fetchCardMarketPrice } from "@/features/tcgApi";
import { useCardsFeed } from "@/features/useCardsFeed";
import {
  fetchCardsFacets,
  type CardsFacets,
  type CardsFeedFilters,
} from "@/context/inventorySync";
import {
  groupCards,
  type GroupAggregate,
  type NameGroup,
  type VersionGroup,
} from "@/features/cardGrouping";
import { formatTimeAgo } from "@/features/formatTime";
import { imageUrl } from "@/features/imageUpload";
import { matchPokemonToFamily } from "@/features/pokemonFamilyMatcher";
import { FAMILY_BY_ID } from "@/constants/pokemonThemeMaps";
import { apiFetch } from "@/lib/apiClient";
import { RequireRole } from "@/lib/auth";

export const Route = createFileRoute("/inventory/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { family?: string; q?: string } => ({
    family:
      typeof search.family === "string" && search.family.length > 0
        ? search.family
        : undefined,
    q:
      typeof search.q === "string" && search.q.length > 0
        ? search.q
        : undefined,
  }),
  component: InventoryListPage,
});

const CONDITIONS: (CardCondition | "All")[] = [
  "All",
  "NM",
  "LP",
  "MP",
  "HP",
  "Damaged",
];
const LISTING_STATUSES = [
  "All",
  "Unlisted",
  "Listed",
  "In Package",
  "Sold",
] as const;
type ListingFilter = (typeof LISTING_STATUSES)[number];

// TNK-109 archive tabs — mirror products.index ArchiveView.
type ArchiveView = "active" | "archived" | "all";
const ARCHIVE_VIEWS: { value: ArchiveView; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

const NO_BIN_SENTINEL = "__no_bin__";

// Card conditions best → worst; the filter only shows the ones present.
const CONDITION_ORDER = ["NM", "LP", "MP", "HP", "Damaged"];

// Market-price bands for the price filter. `test` runs against
// card.marketPrice for the client-side defensive filter; `min`/`max`
// translate to server-side priceMin/priceMax bounds (TNK-155 phase A).
// max=0 means "no upper bound" — matches how the server treats unset.
const PRICE_BANDS: {
  label: string;
  test: (p: number) => boolean;
  min: number;
  max: number;
}[] = [
  { label: "Under $0.25", test: (p) => p < 0.25, min: 0, max: 0.25 },
  {
    label: "$0.25 – $0.50",
    test: (p) => p >= 0.25 && p <= 0.5,
    min: 0.25,
    max: 0.5,
  },
  {
    label: "$0.51 – $1.00",
    test: (p) => p >= 0.51 && p <= 1,
    min: 0.51,
    max: 1,
  },
  { label: "$1 – 5", test: (p) => p > 1 && p < 5, min: 1.01, max: 5 },
  { label: "$5 – 20", test: (p) => p >= 5 && p < 20, min: 5, max: 20 },
  { label: "$20 – 50", test: (p) => p >= 20 && p < 50, min: 20, max: 50 },
  { label: "$50+", test: (p) => p >= 50, min: 50, max: 0 },
];

const uniqSorted = (vals: string[]): string[] =>
  Array.from(new Set(vals.filter(Boolean))).sort((a, b) => a.localeCompare(b));

const CONDITION_COLORS: Record<CardCondition, string> = {
  NM: "#27ae60",
  LP: "#2980b9",
  MP: "#f39c12",
  HP: "#e67e22",
  Damaged: "#e74c3c",
};

type BulkRefreshState =
  | { phase: "idle" }
  | { phase: "running"; current: number; total: number }
  | {
      phase: "done";
      updated: number;
      notFound: number;
      total: number;
      cancelled: boolean;
    };

function InventoryListPage() {
  const navigate = useNavigate();
  const { family: familySearch, q: qSearch } = Route.useSearch();
  const {
    cards,
    lots,
    products,
    storageBins,
    locations,
    bulkUpdateCards,
    bulkDeleteCards,
    bulkUpdateCardLocation,
    bulkUpdateCardLot,
    splitCardStack,
  } = useApp();

  const [search, setSearch] = useState(qSearch ?? "");
  const [archiveView, setArchiveView] = useState<ArchiveView>("active");
  const [filterListing, setFilterListing] = useState<ListingFilter>("All");
  const [filterBin, setFilterBin] = useState<string>("All");
  const [filterLot, setFilterLot] = useState<string>("All");
  const [filterFamily, setFilterFamily] = useState<string | null>(null);
  const [filterRarity, setFilterRarity] = useState("All");
  const [filterSet, setFilterSet] = useState("All");
  const [filterCondition, setFilterCondition] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterGame, setFilterGame] = useState("All");
  const [filterSupertype, setFilterSupertype] = useState("All");
  const [filterGraded, setFilterGraded] = useState("All");
  const [filterPrice, setFilterPrice] = useState("All");
  // TNK-128 operator request: surface cards that still need photos
  // (No Image) and conversely cards already photographed (With Image).
  // Drives the front-image column — back-image is intentionally not
  // gated since some cards have only a front photo on purpose.
  const [filterImage, setFilterImage] = useState("All");

  useEffect(() => {
    setFilterFamily(familySearch ?? null);
  }, [familySearch]);

  const activeFamily = filterFamily
    ? (FAMILY_BY_ID.get(filterFamily) ?? null)
    : null;

  // ── TNK-155 phase A: server-driven feed + facets ─────────────────────────
  //
  // Phase 1 (TNK-126) shipped the foundation. Here in phase A we lift the
  // main inventory grid onto `useCardsFeed`. AppContext.cards stays the
  // source of truth for bulk operations + the selection modal (they
  // iterate the whole inventory); the grid display reads from
  // `feedState.cards` (a single paginated page).
  //
  // Trade-off worth knowing: the 3-layer grouping (Identity → Version →
  // Copy) now operates on the loaded page only. "Charizard ×N" counts
  // reflect the cards visible on this page, not the entire store. The
  // page header surfaces `feedState.total` (the authoritative server-side
  // filter count) so the operator sees the global number.
  //
  // Pokemon family is the only filter that stays client-side — matching
  // requires `matchPokemonToFamily` which is JS-only. The other 12 axes
  // ride the server.
  // Server semantics (cards.ts feed handler):
  //   include unset      → active only (archived_at IS NULL)
  //   include=archived   → all rows (no archive filter)
  //   include=only-archived → archived only (archived_at IS NOT NULL)
  // Client tabs are "Active" / "Archived" / "All" — note the
  // archived-bucket name is intentionally swapped vs the server.
  const include: "archived" | "only-archived" | undefined =
    archiveView === "active"
      ? undefined
      : archiveView === "archived"
        ? "only-archived"
        : "archived";

  // Map current filter state → CardsFeedFilters. Single-select UI →
  // singleton array; "All" → empty array (server treats unset as "all").
  const feedFilters: CardsFeedFilters = useMemo(() => {
    const band = PRICE_BANDS.find((b) => b.label === filterPrice);
    const priceMin = band?.min ?? 0;
    const priceMax = band?.max ?? 0;
    return {
      games: filterGame === "All" ? [] : [filterGame],
      rarities: filterRarity === "All" ? [] : [filterRarity],
      types: filterSupertype === "All" ? [] : [filterSupertype],
      elements: filterType === "All" ? [] : [filterType],
      colors: [],
      conditions: filterCondition === "All" ? [] : [filterCondition],
      listingStatuses: filterListing === "All" ? [] : [filterListing],
      priceMin,
      priceMax,
      foilOnly: false,
      sets: filterSet === "All" ? [] : [filterSet],
      bin: filterBin === "All" ? null : filterBin,
      lot: filterLot === "All" ? null : filterLot,
      graded:
        filterGraded === "Graded"
          ? "graded"
          : filterGraded === "Ungraded"
            ? "ungraded"
            : null,
      image:
        filterImage === "With Image"
          ? "with"
          : filterImage === "No Image"
            ? "without"
            : null,
    };
  }, [
    filterGame,
    filterRarity,
    filterSupertype,
    filterType,
    filterCondition,
    filterListing,
    filterPrice,
    filterSet,
    filterBin,
    filterLot,
    filterGraded,
    filterImage,
  ]);

  const {
    state: feedState,
    loadMore,
    loadingMore,
    refetching,
  } = useCardsFeed({
    query: search,
    sort: "newest",
    filters: feedFilters,
    include,
  });

  const pageCards = feedState.phase === "ready" ? feedState.cards : [];
  const feedTotal = feedState.phase === "ready" ? feedState.total : 0;
  const feedHasMore = feedState.phase === "ready" ? feedState.hasMore : false;

  // Facets — server-driven filter-picker options. Re-fetches when the
  // archive view changes so dropdowns reflect "what's in scope for this
  // tab." Doesn't refetch on every keystroke; the dropdowns stay stable
  // while you narrow.
  const [facets, setFacets] = useState<CardsFacets | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchCardsFacets({ include })
      .then((f) => {
        if (!cancelled) setFacets(f);
      })
      .catch(() => {
        if (!cancelled) setFacets(null);
      });
    return () => {
      cancelled = true;
    };
  }, [include]);

  const [bulkRefresh, setBulkRefresh] = useState<BulkRefreshState>({
    phase: "idle",
  });
  const cancelRef = useRef(false);

  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState<Set<string>>(new Set());
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  const [binPickerVisible, setBinPickerVisible] = useState(false);
  const [lotPickerVisible, setLotPickerVisible] = useState(false);
  const [splitTarget, setSplitTarget] = useState<Card | null>(null);

  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionSearch, setSelectionSearch] = useState("");
  const [selectionCondition, setSelectionCondition] = useState<
    CardCondition | "All"
  >("All");
  // TNK-128: game scope on the Selection modal so the operator can
  // bulk-delete an entire mis-tagged import (e.g. the pre-fix Magic
  // rows that landed as game=Pokemon) without having to type each
  // card name into the search.
  const [selectionGame, setSelectionGame] = useState<string>("All");

  // Reset bin filter if its bin was deleted or renamed.
  useEffect(() => {
    if (filterBin === "All" || filterBin === NO_BIN_SENTINEL) return;
    if (!storageBins.some((b) => b.name === filterBin)) {
      setFilterBin("All");
    }
  }, [storageBins, filterBin]);

  const productMap = useMemo(
    () =>
      Object.fromEntries(products.map((p) => [p.id, p.name || p.productType])),
    [products],
  );

  // Card-attribute filter options, derived from the server-side facets
  // endpoint (TNK-155 phase A) so the dropdowns no longer need the full
  // in-memory cards array. Each axis falls back to an empty list while
  // facets are loading — the picker just shows "All" until then.
  // Conditions stay a fixed enum (server doesn't return a "present
  // conditions" list); the picker shows all five regardless.
  const rarityOptions = facets?.rarities ?? [];
  const setOptions = facets?.sets ?? [];
  const conditionOptions = CONDITION_ORDER;
  const typeOptions = facets?.elements ?? [];
  const gameOptions = facets?.games ?? [];
  const supertypeOptions = facets?.supertypes ?? [];
  const anyGraded = facets?.hasGraded ?? false;

  // Bin/Lot dropdown options (value ≠ label for lots; "No Bin" sentinel).
  // TNK-103 Phase G.4: when 2+ Locations exist, prefix each bin label with
  // its Location name so the operator can tell "Bin 1 @ Home" from
  // "Bin 1 @ Shop" at a glance. The value remains the bin name because the
  // filter still matches via card.storageLocation === filterBin.
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations) map.set(l.id, l.name);
    return map;
  }, [locations]);
  const activeLocationCount = useMemo(
    () => locations.filter((l) => !l.archivedAt).length,
    [locations],
  );
  const binFilterOptions = useMemo<FilterOption[]>(
    () => [
      { value: NO_BIN_SENTINEL, label: "No Bin" },
      ...storageBins.map((b) => {
        if (activeLocationCount <= 1) return b.name;
        const locName = b.locationId
          ? (locationNameById.get(b.locationId) ?? "")
          : "";
        return {
          value: b.name,
          label: locName ? `${locName} · ${b.name}` : b.name,
        };
      }),
    ],
    [storageBins, activeLocationCount, locationNameById],
  );
  const lotFilterOptions = useMemo<FilterOption[]>(
    () =>
      [...lots]
        .sort(
          (a, b) =>
            new Date(b.purchaseDate).getTime() -
            new Date(a.purchaseDate).getTime(),
        )
        .map((l) => ({
          value: l.id,
          label: l.seller
            ? `${l.seller} · ${new Date(l.purchaseDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
            : new Date(l.purchaseDate).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
        })),
    [lots],
  );

  // TNK-109: archive tab counts for the badge next to each tab label.
  const archiveCounts = useMemo(() => {
    let archived = 0;
    for (const c of cards) {
      if (c.archivedAt) archived++;
    }
    return { archived, active: cards.length - archived };
  }, [cards]);

  // TNK-155 phase A: filter pipeline is now server-driven via
  // `useCardsFeed`. The server handles archive view, listing status,
  // bin, lot, rarity, set, condition, type, element/supertype, game,
  // graded, image, price band, and the `q` search (name + setName).
  //
  // Family stays client-side — `matchPokemonToFamily` is JS-side
  // matching against the Pokemon family map. Applied per-loaded-page;
  // it narrows visible results but doesn't drive the server query, so
  // the page header's "X of N matching" denominator slightly overcounts
  // when a family filter is active. Acceptable for a niche filter.
  //
  // Card-id + storageLocation free-text search no longer fold into the
  // `q` query — operators looking for a specific card-id should use the
  // single-card lookup landing in phase B, or filter by Bin.
  const filtered = useMemo(() => {
    if (!filterFamily) return pageCards;
    return pageCards.filter((c) => {
      if (c.game !== "Pokemon") return false;
      const fam = matchPokemonToFamily(c.name);
      return fam?.familyId === filterFamily;
    });
  }, [pageCards, filterFamily]);

  const anyFilterActive =
    search.trim().length > 0 ||
    filterFamily !== null ||
    [
      filterListing,
      filterBin,
      filterLot,
      filterRarity,
      filterSet,
      filterCondition,
      filterType,
      filterGame,
      filterSupertype,
      filterGraded,
      filterPrice,
      filterImage,
    ].some((f) => f !== "All");

  const resetFilters = () => {
    setSearch("");
    setFilterListing("All");
    setFilterBin("All");
    setFilterLot("All");
    setFilterRarity("All");
    setFilterSet("All");
    setFilterCondition("All");
    setFilterType("All");
    setFilterGame("All");
    setFilterSupertype("All");
    setFilterGraded("All");
    setFilterPrice("All");
    setFilterImage("All");
    if (filterFamily) {
      setFilterFamily(null);
      navigate({ to: "/inventory", search: { family: undefined } });
    }
  };

  // ── 3-layer grouping (Identity → Version → Copy) ─────────────────────────
  // After the existing filter pipeline runs, fold the surviving cards into
  // NameGroup[] → VersionGroup[] → Card[]. Filters keep operating per-card
  // upstream; this is purely render structure.
  const grouped = useMemo(() => groupCards(filtered), [filtered]);

  const [expandedNames, setExpandedNames] = useState<Set<string>>(new Set());
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(
    new Set(),
  );

  const toggleNameExpand = (key: string) => {
    setExpandedNames((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleVersionExpand = (key: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Auto-expand every group with matching descendants when text search is
  // non-empty. Pure derivation from `grouped` — no setState needed.
  const isSearching = search.trim().length > 0;
  const effectiveExpandedNames = useMemo(() => {
    if (!isSearching) return expandedNames;
    return new Set(grouped.map((g) => g.key));
  }, [isSearching, expandedNames, grouped]);
  const effectiveExpandedVersions = useMemo(() => {
    if (!isSearching) return expandedVersions;
    const all = new Set<string>();
    for (const g of grouped) for (const v of g.versions) all.add(v.key);
    return all;
  }, [isSearching, expandedVersions, grouped]);

  const eligibleCards = useMemo(
    () =>
      cards.filter(
        (c) =>
          c.listingStatus === "Unlisted" || c.listingStatus === "In Package",
      ),
    [cards],
  );

  const selectionFiltered = useMemo(() => {
    let result = [...eligibleCards];
    if (selectionCondition !== "All")
      result = result.filter((c) => c.condition === selectionCondition);
    if (selectionGame !== "All")
      result = result.filter((c) => c.game === selectionGame);
    if (selectionSearch.trim()) {
      const q = selectionSearch.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.setName.toLowerCase().includes(q) ||
          c.cardNumber.toLowerCase().includes(q) ||
          c.storageLocation.toLowerCase().includes(q),
      );
    }
    return result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [eligibleCards, selectionSearch, selectionCondition, selectionGame]);

  // ── Delete mode ───────────────────────────────────────────────────────────

  const isDeleteEligible = (card: { listingStatus: string }) =>
    card.listingStatus !== "In Package" && card.listingStatus !== "Sold";

  const handleEnterDeleteMode = () => {
    setDeleteSelected(new Set());
    setDeleteConfirming(false);
    setDeleteMode(true);
  };

  const handleExitDeleteMode = () => {
    setDeleteMode(false);
    setDeleteSelected(new Set());
    setDeleteConfirming(false);
  };

  // Escape key exits delete mode (replaces RN BackHandler intercept).
  useEffect(() => {
    if (!deleteMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleExitDeleteMode();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [deleteMode]);

  const handleDeleteToggle = (id: string) => {
    const card = filtered.find((c) => c.id === id);
    if (!card || !isDeleteEligible(card)) return;
    setDeleteSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDeleteConfirming(false);
  };

  // Roll-up checkbox state for a group: returns "off" / "all" / "some"
  // (indeterminate) based on which eligible descendants are currently
  // selected for delete.
  type GroupSelectionState = "off" | "all" | "some";
  const groupSelectionState = (groupCardsList: Card[]): GroupSelectionState => {
    const eligibles = groupCardsList.filter(isDeleteEligible);
    if (eligibles.length === 0) return "off";
    const selectedCount = eligibles.filter((c) =>
      deleteSelected.has(c.id),
    ).length;
    if (selectedCount === 0) return "off";
    if (selectedCount === eligibles.length) return "all";
    return "some";
  };
  const toggleGroupSelection = (groupCardsList: Card[]) => {
    const eligibles = groupCardsList.filter(isDeleteEligible);
    if (eligibles.length === 0) return;
    const allOn = groupSelectionState(groupCardsList) === "all";
    setDeleteSelected((prev) => {
      const next = new Set(prev);
      for (const c of eligibles) {
        if (allOn) next.delete(c.id);
        else next.add(c.id);
      }
      return next;
    });
    setDeleteConfirming(false);
  };

  const eligibleFiltered = filtered.filter(isDeleteEligible);
  const allFilteredDeleteSelected =
    eligibleFiltered.length > 0 &&
    eligibleFiltered.every((c) => deleteSelected.has(c.id));

  const handleDeleteSelectAll = () => {
    if (allFilteredDeleteSelected) {
      setDeleteSelected((prev) => {
        const next = new Set(prev);
        eligibleFiltered.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setDeleteSelected((prev) => {
        const next = new Set(prev);
        eligibleFiltered.forEach((c) => next.add(c.id));
        return next;
      });
    }
    setDeleteConfirming(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirming) {
      setDeleteConfirming(true);
      return;
    }
    bulkDeleteCards([...deleteSelected]);
    handleExitDeleteMode();
  };

  // ── Price refresh ─────────────────────────────────────────────────────────

  const handleOpenSelectionModal = () => {
    setSelectedIds(new Set(eligibleCards.map((c) => c.id)));
    setSelectionSearch("");
    setSelectionCondition("All");
    setSelectionGame("All");
    setSelectionModalVisible(true);
  };

  const handleToggleCard = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected =
    selectionFiltered.length > 0 &&
    selectionFiltered.every((c) => selectedIds.has(c.id));

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectionFiltered.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectionFiltered.forEach((c) => next.add(c.id));
        return next;
      });
    }
  };

  const handleStartRefresh = () => {
    setSelectionModalVisible(false);
    const toRefresh = eligibleCards.filter((c) => selectedIds.has(c.id));
    if (toRefresh.length === 0) return;
    runBulkRefresh(toRefresh);
  };

  const runBulkRefresh = async (eligible: Card[]) => {
    cancelRef.current = false;
    setBulkRefresh({ phase: "running", current: 0, total: eligible.length });

    let updated = 0;
    let notFound = 0;
    const patches: CardPatch[] = [];

    for (let i = 0; i < eligible.length; i++) {
      if (cancelRef.current) break;
      setBulkRefresh({
        phase: "running",
        current: i + 1,
        total: eligible.length,
      });

      const card = eligible[i];
      try {
        let price: number | null = null;
        let priceSource: PriceSource = "TCGplayer";

        price = await fetchCardMarketPrice(
          card.setCode,
          card.cardNumber,
          card.name,
        );

        if (price == null) {
          priceSource = "eBay Sold";
          const q = [card.name, card.setName].filter(Boolean).join(" ");
          try {
            const ebayRes = await apiFetch(
              `/api/ebay/sold-price?q=${encodeURIComponent(q)}&categoryId=183050`,
            );
            if (ebayRes.ok) {
              const ebayData = (await ebayRes.json()) as {
                price: number | null;
                count: number;
              };
              price = ebayData.price;
            }
          } catch {
            // fall through; price stays null
          }
        }

        if (price != null) {
          patches.push({
            id: card.id,
            patch: {
              marketPrice: price,
              priceSource,
              pricedAt: new Date().toISOString(),
              expectedSalePrice: conditionAdjust(price, card.condition),
            },
          });
          updated++;
        } else {
          notFound++;
        }
      } catch {
        notFound++;
      }

      // 200ms throttle between requests.
      if (i < eligible.length - 1 && !cancelRef.current) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }

    if (patches.length > 0) {
      bulkUpdateCards(patches);
    }

    const wasCancelled = cancelRef.current;
    setBulkRefresh({
      phase: "done",
      updated,
      notFound,
      total: eligible.length,
      cancelled: wasCancelled,
    });
  };

  const handleCancelRefresh = () => {
    cancelRef.current = true;
  };

  const handleDismissRefresh = () => {
    setBulkRefresh({ phase: "idle" });
  };

  const eligibleCount = eligibleCards.length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2.5 border-b border-border pb-3">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {deleteMode
              ? deleteSelected.size > 0
                ? `${deleteSelected.size} selected`
                : "Select Cards"
              : "Card Inventory"}
          </h1>
          <div className="flex items-center gap-2">
            {deleteMode ? (
              <>
                <button
                  type="button"
                  onClick={handleDeleteSelectAll}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-muted"
                >
                  {allFilteredDeleteSelected ? (
                    <MinusSquare className="h-3.5 w-3.5" />
                  ) : (
                    <CheckSquare className="h-3.5 w-3.5" />
                  )}
                  {allFilteredDeleteSelected ? "Deselect All" : "Select All"}
                </button>
                <button
                  type="button"
                  onClick={handleExitDeleteMode}
                  className="rounded-md bg-muted px-3 py-1.5 text-xs font-semibold text-foreground hover:opacity-90"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/inventory/bulk"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                >
                  <Zap className="h-3.5 w-3.5" />
                  Bulk
                </Link>
                <Link
                  to="/inventory/create"
                  className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground hover:opacity-90"
                  aria-label="Add card"
                >
                  <Plus className="h-[18px] w-[18px]" />
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {deleteMode
              ? `${filtered.length} card${filtered.length !== 1 ? "s" : ""} shown`
              : anyFilterActive
                ? `${filtered.length.toLocaleString()} of ${feedTotal.toLocaleString()} matching loaded`
                : `${feedTotal.toLocaleString()} cards tracked`}
            {(refetching || loadingMore) && (
              <RefreshCw className="ml-1.5 inline h-3 w-3 animate-spin" />
            )}
          </p>
          {!deleteMode && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleOpenSelectionModal}
                disabled={
                  bulkRefresh.phase === "running" || eligibleCount === 0
                }
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                title="Refresh prices"
              >
                <RefreshCw className="h-3 w-3" />
                Sync
              </button>
              <RequireRole min="manager">
                <button
                  type="button"
                  onClick={handleEnterDeleteMode}
                  disabled={cards.length === 0}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  <CheckSquare className="h-3 w-3" />
                  Select
                </button>
              </RequireRole>
            </div>
          )}
        </div>

        {deleteMode && (
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <CheckSquare className="h-[15px] w-[15px]" />
            <span>
              {deleteSelected.size > 0
                ? `${deleteSelected.size} of ${filtered.length} selected`
                : "Tap cards below to select them"}
            </span>
          </div>
        )}
        {/*
          TNK-128: filter row + search + archive tabs stay visible in
          delete mode so the operator can narrow the bulk selection by
          Game / Lot / Bin / Listing before tapping cards. Previously
          the entire filter band was hidden inside the !deleteMode
          branch, which made bulk-deleting an entire mis-tagged import
          impossible without manual one-by-one tapping.
        */}
        {/* TNK-109 archive tabs — Active / Archived / All with count badges. */}
        <div className="flex border-b border-border">
          {ARCHIVE_VIEWS.map((v) => {
            const active = archiveView === v.value;
            const count =
              v.value === "active"
                ? archiveCounts.active
                : v.value === "archived"
                  ? archiveCounts.archived
                  : cards.length;
            return (
              <button
                key={v.value}
                type="button"
                onClick={() => setArchiveView(v.value)}
                className={`relative -mb-px border-b-2 px-3 py-1.5 text-xs font-semibold ${
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {v.label}
                {count > 0 && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      active
                        ? "bg-primary/15 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2 pb-1">
          <FilterSelect
            label="Listing"
            value={filterListing}
            options={LISTING_STATUSES.filter((s) => s !== "All")}
            onChange={(v) => setFilterListing(v as ListingFilter)}
          />
          {storageBins.length > 0 && (
            <FilterSelect
              label="Bin"
              value={filterBin}
              options={binFilterOptions}
              onChange={setFilterBin}
            />
          )}
          {lots.length > 0 && (
            <FilterSelect
              label="Lot"
              value={filterLot}
              options={lotFilterOptions}
              onChange={setFilterLot}
              allLabel="All Lots"
            />
          )}
          {rarityOptions.length > 0 && (
            <FilterSelect
              label="Rarity"
              value={filterRarity}
              options={rarityOptions}
              onChange={setFilterRarity}
            />
          )}
          {setOptions.length > 0 && (
            <FilterSelect
              label="Set"
              value={filterSet}
              options={setOptions}
              onChange={setFilterSet}
            />
          )}
          {conditionOptions.length > 0 && (
            <FilterSelect
              label="Condition"
              value={filterCondition}
              options={conditionOptions}
              onChange={setFilterCondition}
            />
          )}
          {typeOptions.length > 0 && (
            <FilterSelect
              label="Card Type"
              value={filterType}
              options={typeOptions}
              onChange={setFilterType}
            />
          )}
          <FilterSelect
            label="Price"
            value={filterPrice}
            options={PRICE_BANDS.map((b) => b.label)}
            onChange={setFilterPrice}
            allLabel="Any"
          />
          {/*
                Always render the Game filter when inventory exists,
                even if all visible cards share a single game tag. A
                single-game inventory is often the result of a
                mis-tagged import (e.g. MTG rows landing as Pokemon
                before TNK-128 phase A); hiding the filter at that
                moment removes the exact tool the operator needs to
                isolate the bad batch for delete-mode / lot-reassign.
              */}
          {gameOptions.length > 0 && (
            <FilterSelect
              label="Game"
              value={filterGame}
              options={gameOptions}
              onChange={setFilterGame}
            />
          )}
          {supertypeOptions.length > 0 && (
            <FilterSelect
              label="Supertype"
              value={filterSupertype}
              options={supertypeOptions}
              onChange={setFilterSupertype}
            />
          )}
          <FilterSelect
            label="Image"
            value={filterImage}
            options={["With Image", "No Image"]}
            onChange={setFilterImage}
          />
          {anyGraded && (
            <FilterSelect
              label="Graded"
              value={filterGraded}
              options={["Graded", "Raw"]}
              onChange={setFilterGraded}
            />
          )}
          {anyFilterActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 self-end rounded-md border border-border bg-muted px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-card hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
        </div>

        {activeFamily && (
          <div className="flex items-center gap-2 rounded-md border border-secondary bg-secondary/10 px-3 py-2 text-xs">
            <GitBranch className="h-3.5 w-3.5 text-secondary-foreground" />
            <span className="flex-1 truncate text-foreground">
              Family:{" "}
              <span className="font-semibold">{activeFamily.label}</span>
            </span>
            <button
              type="button"
              onClick={() => {
                setFilterFamily(null);
                navigate({
                  to: "/inventory",
                  search: { family: undefined },
                });
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear family filter"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {feedState.phase === "loading" ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin" />
          </div>
        ) : feedState.phase === "error" ? (
          <EmptyState
            icon={<AlertCircle className="h-8 w-8" />}
            title="Couldn't load cards"
            description="Check your connection and refresh."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<CreditCard className="h-8 w-8" />}
            title={
              anyFilterActive || search.trim().length > 0
                ? "No cards match your filters"
                : "No cards yet"
            }
            description={
              anyFilterActive || search.trim().length > 0
                ? "Try clearing some filters or broaden the search."
                : "Add individual card inventory for valuable singles"
            }
          />
        ) : (
          <div className="space-y-2 pb-32">
            {grouped.map((group) => {
              const nameExpanded = effectiveExpandedNames.has(group.key);
              const nameSel = groupSelectionState(group.cards);
              return (
                <div
                  key={group.key}
                  className="overflow-hidden rounded-md border border-border bg-card"
                >
                  <NameGroupHeader
                    group={group}
                    expanded={nameExpanded}
                    onToggleExpand={() => toggleNameExpand(group.key)}
                    deleteMode={deleteMode}
                    selectionState={nameSel}
                    onToggleSelection={() => toggleGroupSelection(group.cards)}
                  />

                  {nameExpanded && (
                    <div className="divide-y divide-border border-t border-border">
                      {group.versions.map((version) => {
                        const versionExpanded = effectiveExpandedVersions.has(
                          version.key,
                        );
                        const vSel = groupSelectionState(version.cards);
                        return (
                          <div key={version.key}>
                            <VersionGroupHeader
                              version={version}
                              expanded={versionExpanded}
                              onToggleExpand={() =>
                                toggleVersionExpand(version.key)
                              }
                              deleteMode={deleteMode}
                              selectionState={vSel}
                              onToggleSelection={() =>
                                toggleGroupSelection(version.cards)
                              }
                            />

                            {versionExpanded && (
                              <div className="space-y-1.5 bg-muted/30 px-3 py-2">
                                {version.cards.map((card) => {
                                  const isDeleteSelected = deleteSelected.has(
                                    card.id,
                                  );
                                  const deleteLocked =
                                    deleteMode && !isDeleteEligible(card);
                                  // TNK-109: archived rows render with a muted
                                  // bg + reduced opacity so the Archived tab
                                  // reads as a graveyard, not active inventory.
                                  const archivedClass = card.archivedAt
                                    ? " opacity-60"
                                    : "";
                                  const cardClass = isDeleteSelected
                                    ? "border-red-500 bg-red-500/10"
                                    : deleteLocked
                                      ? "border-border bg-muted/50 opacity-55"
                                      : `border-border bg-card hover:bg-card/80${archivedClass}`;
                                  return (
                                    <div key={card.id} className="space-y-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (deleteMode) {
                                            handleDeleteToggle(card.id);
                                          } else {
                                            navigate({
                                              to: "/inventory/$id",
                                              params: { id: card.id },
                                            });
                                          }
                                        }}
                                        className={`flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors ${cardClass}`}
                                      >
                                        {deleteMode && (
                                          <span
                                            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border"
                                            style={{
                                              borderColor: deleteLocked
                                                ? undefined
                                                : isDeleteSelected
                                                  ? "#e74c3c"
                                                  : undefined,
                                              backgroundColor: isDeleteSelected
                                                ? "#e74c3c"
                                                : undefined,
                                            }}
                                          >
                                            {deleteLocked ? (
                                              card.listingStatus === "Sold" ? (
                                                <span className="text-[7px] font-bold text-muted-foreground">
                                                  SOLD
                                                </span>
                                              ) : (
                                                <Lock className="h-[9px] w-[9px] text-muted-foreground" />
                                              )
                                            ) : isDeleteSelected ? (
                                              <CheckCircle className="h-2.5 w-2.5 text-white" />
                                            ) : null}
                                          </span>
                                        )}
                                        <div className="flex-1 space-y-1">
                                          <div className="text-sm font-semibold text-foreground">
                                            {card.condition} · ×{card.quantity}
                                          </div>
                                          <div className="flex flex-wrap gap-1.5 pt-1">
                                            <Badge
                                              label={card.condition}
                                              variant={
                                                card.condition === "NM"
                                                  ? "success"
                                                  : card.condition === "Damaged"
                                                    ? "danger"
                                                    : "default"
                                              }
                                            />
                                            <Badge
                                              label={card.rarity}
                                              variant="muted"
                                            />
                                            {card.listingStatus ===
                                              "Listed" && (
                                              <Badge
                                                label="Listed"
                                                variant="accent"
                                              />
                                            )}
                                            {card.listingStatus ===
                                              "In Package" && (
                                              <Badge
                                                label={
                                                  card.packagedProductId &&
                                                  productMap[
                                                    card.packagedProductId
                                                  ]
                                                    ? `📦 ${productMap[card.packagedProductId]}`
                                                    : "In Package"
                                                }
                                                variant="warning"
                                              />
                                            )}
                                            {card.listingStatus === "Sold" && (
                                              <Badge
                                                label="Sold"
                                                variant="success"
                                              />
                                            )}
                                            {card.storageLocation.trim() &&
                                              (() => {
                                                // TNK-103 Phase G.4: show
                                                // "Location · Bin" on the
                                                // badge when 2+ Locations
                                                // exist; otherwise just the
                                                // bin name.
                                                const binName =
                                                  card.storageLocation.trim();
                                                const bin = storageBins.find(
                                                  (b) => b.name === binName,
                                                );
                                                const locName =
                                                  activeLocationCount > 1 &&
                                                  bin?.locationId
                                                    ? locationNameById.get(
                                                        bin.locationId,
                                                      )
                                                    : undefined;
                                                return (
                                                  <Badge
                                                    label={
                                                      locName
                                                        ? `${locName} · ${binName}`
                                                        : binName
                                                    }
                                                    variant="muted"
                                                  />
                                                );
                                              })()}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-0.5 text-right">
                                          <div className="text-base font-semibold text-foreground">
                                            ${card.expectedSalePrice.toFixed(2)}
                                          </div>
                                          {card.marketPrice > 0 && (
                                            <div className="text-[11px] text-muted-foreground">
                                              30-Day Avg $
                                              {card.marketPrice.toFixed(2)}
                                            </div>
                                          )}
                                          {card.pricedAt && (
                                            <div className="text-[10px] text-muted-foreground">
                                              Refreshed{" "}
                                              {formatTimeAgo(card.pricedAt)}
                                            </div>
                                          )}
                                        </div>
                                      </button>
                                      {!deleteMode && card.quantity > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => setSplitTarget(card)}
                                          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                                        >
                                          <Scissors className="h-3 w-3" />
                                          Split stack of {card.quantity}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Load-more sentinel (TNK-155 phase A). Server-paginated
                feed; `feedHasMore` flips to false at end of results.
                Family-filter narrowing happens client-side, so when
                a family is active the "no more" state can be reached
                while the server still has unfiltered pages — bumping
                Load more keeps pulling pages the family filter can
                further narrow. */}
            {feedHasMore && (
              <div className="flex justify-center py-4">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw className="h-3 w-3 animate-spin" /> Loading…
                    </>
                  ) : (
                    <>Load more</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk-action bottom bar */}
      {deleteMode && (
        <div
          className={`sticky bottom-0 border-t px-4 py-3 ${
            deleteConfirming
              ? "border-red-700 bg-red-600 text-white"
              : "border-border bg-card"
          }`}
        >
          {deleteConfirming ? (
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-sm">
                Tap again to confirm — this cannot be undone.
              </span>
              <button
                type="button"
                onClick={() => setDeleteConfirming(false)}
                className="rounded border border-white/40 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="inline-flex items-center gap-1.5 rounded bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:opacity-90"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete {deleteSelected.size} card
                {deleteSelected.size !== 1 ? "s" : ""}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 text-xs text-muted-foreground">
                {(() => {
                  const activeCount = [...deleteSelected].filter((id) => {
                    const c = cards.find((x) => x.id === id);
                    return c && c.listingStatus === "Listed";
                  }).length;
                  if (activeCount > 0) {
                    return (
                      <div>
                        <div>
                          {deleteSelected.size} card
                          {deleteSelected.size !== 1 ? "s" : ""} selected
                        </div>
                        <div className="mt-0.5 text-[11px] font-semibold text-amber-500">
                          {activeCount} active — will be removed from listing
                          {activeCount !== 1 ? "s" : ""}
                        </div>
                      </div>
                    );
                  }
                  return deleteSelected.size > 0
                    ? `${deleteSelected.size} card${deleteSelected.size !== 1 ? "s" : ""} selected`
                    : "Tap cards to select";
                })()}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (deleteSelected.size === 0) return;
                  setDeleteConfirming(false);
                  setBinPickerVisible(true);
                }}
                disabled={deleteSelected.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-40"
              >
                <MapPin className="h-3.5 w-3.5" />
                Edit Location
              </button>
              <button
                type="button"
                onClick={() => {
                  if (deleteSelected.size === 0) return;
                  setDeleteConfirming(false);
                  setLotPickerVisible(true);
                }}
                disabled={deleteSelected.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-40"
              >
                <Layers className="h-3.5 w-3.5" />
                Reassign Lot
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteSelected.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleteSelected.size > 0
                  ? `Delete ${deleteSelected.size} card${deleteSelected.size !== 1 ? "s" : ""}`
                  : "Delete"}
              </button>
            </div>
          )}
        </div>
      )}

      <BinPickerModal
        open={binPickerVisible}
        value=""
        onSelect={(binName) => {
          if (!binName.trim()) return;
          bulkUpdateCardLocation([...deleteSelected], binName.trim());
          handleExitDeleteMode();
        }}
        onClose={() => setBinPickerVisible(false)}
      />

      <LotReassignModal
        open={lotPickerVisible}
        lots={lots}
        selectedCount={deleteSelected.size}
        onClose={() => setLotPickerVisible(false)}
        onSelect={(lotId) => {
          bulkUpdateCardLot([...deleteSelected], lotId);
          setLotPickerVisible(false);
          handleExitDeleteMode();
        }}
      />

      <SelectionModal
        open={selectionModalVisible}
        cards={selectionFiltered}
        eligibleCount={eligibleCards.length}
        selectedIds={selectedIds}
        selectionSearch={selectionSearch}
        setSelectionSearch={setSelectionSearch}
        selectionCondition={selectionCondition}
        setSelectionCondition={setSelectionCondition}
        selectionGame={selectionGame}
        setSelectionGame={setSelectionGame}
        gameOptions={gameOptions}
        allVisibleSelected={allVisibleSelected}
        onToggleCard={handleToggleCard}
        onSelectAll={handleSelectAll}
        onClose={() => setSelectionModalVisible(false)}
        onRefresh={handleStartRefresh}
      />

      <RefreshProgressModal
        state={bulkRefresh}
        onCancel={handleCancelRefresh}
        onDismiss={handleDismissRefresh}
      />

      <SplitStackModal
        card={splitTarget}
        onClose={() => setSplitTarget(null)}
        onConfirm={() => {
          if (splitTarget) splitCardStack(splitTarget.id);
          setSplitTarget(null);
        }}
      />
    </div>
  );
}

function SplitStackModal({
  card,
  onClose,
  onConfirm,
}: {
  card: Card | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={card !== null}
      onClose={onClose}
      ariaLabel="Split stack"
      panelClassName="w-[calc(100vw-2rem)] max-w-sm p-5"
    >
      {card && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">
              Split stack of {card.quantity}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Split this stack of {card.quantity} {card.name} into {card.quantity}{" "}
            separate records? Each can then carry its own photo, condition,
            location, cost basis, and status.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              <Scissors className="h-3.5 w-3.5" />
              Split into {card.quantity}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────────

function LotReassignModal({
  open,
  lots,
  selectedCount,
  onClose,
  onSelect,
}: {
  open: boolean;
  lots: ReturnType<typeof useApp>["lots"];
  selectedCount: number;
  onClose: () => void;
  onSelect: (lotId: string) => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Reassign lot"
      panelClassName="w-[92vw] max-w-md max-h-[80vh] overflow-hidden flex flex-col"
    >
      <div className="flex items-start justify-between border-b border-border px-5 py-3.5">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Reassign Lot
          </h3>
          <p className="text-xs text-muted-foreground">
            {selectedCount} card{selectedCount !== 1 ? "s" : ""} → pick a lot
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-muted p-1.5 text-muted-foreground hover:bg-muted/80"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {lots.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-muted-foreground">
            No lots yet — create one first.
          </p>
        ) : (
          [...lots]
            .sort(
              (a, b) =>
                new Date(b.purchaseDate).getTime() -
                new Date(a.purchaseDate).getTime(),
            )
            .map((lot) => (
              <button
                key={lot.id}
                type="button"
                onClick={() => onSelect(lot.id)}
                className="flex w-full items-center gap-3 border-b border-border px-5 py-3 text-left hover:bg-muted/40"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Package className="h-4 w-4" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    {lot.seller || "Unknown seller"}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {new Date(lot.purchaseDate).toLocaleDateString()}
                    {lot.lotType ? ` · ${lot.lotType}` : ""}
                    {` · $${lot.purchaseCost.toFixed(2)}`}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))
        )}
      </div>
    </Modal>
  );
}

function SelectionModal({
  open,
  cards,
  eligibleCount,
  selectedIds,
  selectionSearch,
  setSelectionSearch,
  selectionCondition,
  setSelectionCondition,
  selectionGame,
  setSelectionGame,
  gameOptions,
  allVisibleSelected,
  onToggleCard,
  onSelectAll,
  onClose,
  onRefresh,
}: {
  open: boolean;
  cards: Card[];
  eligibleCount: number;
  selectedIds: Set<string>;
  selectionSearch: string;
  setSelectionSearch: (s: string) => void;
  selectionCondition: CardCondition | "All";
  setSelectionCondition: (c: CardCondition | "All") => void;
  selectionGame: string;
  setSelectionGame: (g: string) => void;
  gameOptions: string[];
  allVisibleSelected: boolean;
  onToggleCard: (id: string) => void;
  onSelectAll: () => void;
  onClose: () => void;
  onRefresh: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Select cards to refresh"
      panelClassName="w-[92vw] max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
    >
      <div className="flex items-start justify-between border-b border-border px-5 py-3.5">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Select Cards to Refresh
          </h3>
          <p className="text-xs text-muted-foreground">
            {selectedIds.size} of {eligibleCount} selected
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-muted p-1.5 text-muted-foreground hover:bg-muted/80"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-2.5 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
          <Search className="h-[15px] w-[15px] text-muted-foreground" />
          <input
            type="search"
            value={selectionSearch}
            onChange={(e) => setSelectionSearch(e.target.value)}
            placeholder="Filter cards..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {selectionSearch.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectionSearch("")}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear filter"
            >
              <X className="h-[15px] w-[15px]" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {CONDITIONS.map((cond) => {
            const active = selectionCondition === cond;
            return (
              <button
                key={cond}
                type="button"
                onClick={() => setSelectionCondition(cond)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {cond}
              </button>
            );
          })}
        </div>
        {/*
          TNK-128: Game scope so the operator can bulk-select an entire
          mis-tagged import (e.g. legacy MTG rows that landed as
          game=Pokemon before the parser fix) without typing each name.
          Always renders when any cards exist — single-game inventories
          are often the *symptom* of a mis-tagged import, so hiding the
          filter at that moment removes the exact tool needed to fix it.
        */}
        {gameOptions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {["All", ...gameOptions].map((g) => {
              const active = selectionGame === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setSelectionGame(g)}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {g}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {cards.length} card{cards.length !== 1 ? "s" : ""} shown
          </span>
          <button
            type="button"
            onClick={onSelectAll}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-primary hover:bg-muted"
          >
            {allVisibleSelected ? (
              <MinusSquare className="h-3.5 w-3.5" />
            ) : (
              <CheckSquare className="h-3.5 w-3.5" />
            )}
            {allVisibleSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {cards.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <Inbox className="h-8 w-8" />
            <p className="text-sm">No eligible cards match your filters</p>
          </div>
        ) : (
          cards.map((card) => {
            const isSelected = selectedIds.has(card.id);
            const condColor = CONDITION_COLORS[card.condition];
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => onToggleCard(card.id)}
                className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors ${
                  isSelected
                    ? "border-primary bg-card"
                    : "border-border bg-background hover:bg-card"
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    isSelected
                      ? "border-primary bg-primary"
                      : "border-border bg-transparent"
                  }`}
                >
                  {isSelected && (
                    <CheckCircle className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {card.name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {card.setName} · {card.cardNumber}
                    {card.storageLocation ? ` · ${card.storageLocation}` : ""}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                    style={{
                      backgroundColor: `${condColor}22`,
                      color: condColor,
                    }}
                  >
                    {card.condition}
                  </span>
                  {card.marketPrice > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ${card.marketPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="flex gap-2 border-t border-border px-5 py-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-md bg-muted py-2.5 text-sm font-semibold text-muted-foreground hover:opacity-90"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onRefresh}
          disabled={selectedIds.size === 0}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh{" "}
          {selectedIds.size > 0
            ? `${selectedIds.size} Card${selectedIds.size !== 1 ? "s" : ""}`
            : ""}
        </button>
      </div>
    </Modal>
  );
}

function RefreshProgressModal({
  state,
  onCancel,
  onDismiss,
}: {
  state: BulkRefreshState;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  if (state.phase === "idle") return null;
  return (
    <Modal
      open
      onClose={state.phase === "done" ? onDismiss : () => {}}
      ariaLabel="Refresh progress"
      panelClassName="w-[85vw] max-w-sm p-6"
    >
      {state.phase === "running" && (
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <h3 className="text-lg font-semibold text-foreground">
            Refreshing Prices
          </h3>
          <p className="text-sm text-muted-foreground">
            Updating {state.current} of {state.total}...
          </p>
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 w-full rounded-md bg-muted py-2.5 text-sm font-semibold text-muted-foreground hover:opacity-90"
          >
            Cancel
          </button>
        </div>
      )}
      {state.phase === "done" && (
        <div className="flex flex-col items-center gap-3 text-center">
          <span
            className={`flex h-16 w-16 items-center justify-center rounded-full ${
              state.cancelled
                ? "bg-muted"
                : state.updated > 0
                  ? "bg-emerald-500/20"
                  : "bg-muted"
            }`}
          >
            {state.cancelled ? (
              <Slash className="h-8 w-8 text-muted-foreground" />
            ) : state.updated > 0 ? (
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            ) : (
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
            )}
          </span>
          <h3 className="text-lg font-semibold text-foreground">
            {state.cancelled ? "Refresh Cancelled" : "Refresh Complete"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {state.cancelled
              ? `Processed ${state.updated + state.notFound} of ${state.total} — ${state.updated} updated${state.notFound > 0 ? `, ${state.notFound} not found` : ""}`
              : `${state.updated} updated${state.notFound > 0 ? `, ${state.notFound} not found` : ""}`}
          </p>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-2 w-full rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  );
}

// ─── 3-layer group headers ────────────────────────────────────────────────
// L1 = Name. L2 = Version (printing). L3 = individual Card record, rendered
// inline by the route. Headers render the rolled-up thumbnail + qty + value +
// status pills, plus a checkbox in delete mode that toggles every descendant.

type GroupSelectionState = "off" | "all" | "some";

function GroupCheckbox({
  state,
  onClick,
}: {
  state: GroupSelectionState;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label="Select group"
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
        state === "all"
          ? "border-red-500 bg-red-500 text-white"
          : state === "some"
            ? "border-red-500 bg-red-500/30"
            : "border-border bg-card"
      }`}
    >
      {state === "all" && <CheckCircle className="h-2.5 w-2.5" />}
      {state === "some" && <MinusSquare className="h-2.5 w-2.5 text-red-600" />}
    </button>
  );
}

function StatusPills({ stats }: { stats: GroupAggregate["statusBreakdown"] }) {
  const entries: { label: string; count: number; cls: string }[] = [];
  if (stats.Listed > 0)
    entries.push({
      label: "Listed",
      count: stats.Listed,
      cls: "bg-blue-500/15 text-blue-600",
    });
  if (stats.Unlisted > 0)
    entries.push({
      label: "Unlisted",
      count: stats.Unlisted,
      cls: "bg-muted text-muted-foreground",
    });
  if (stats["In Package"] > 0)
    entries.push({
      label: "In Package",
      count: stats["In Package"],
      cls: "bg-amber-500/15 text-amber-600",
    });
  if (stats.Sold > 0)
    entries.push({
      label: "Sold",
      count: stats.Sold,
      cls: "bg-emerald-500/15 text-emerald-600",
    });
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {entries.map((e) => (
        <span
          key={e.label}
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${e.cls}`}
        >
          {e.count} {e.label}
        </span>
      ))}
    </div>
  );
}

function GroupThumbnail({ ref }: { ref: string | null }) {
  if (!ref) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-border bg-muted/50 text-muted-foreground">
        <CreditCard className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={imageUrl(ref) ?? undefined}
      alt=""
      className="h-10 w-10 shrink-0 rounded border border-border object-cover"
      loading="lazy"
    />
  );
}

function NameGroupHeader({
  group,
  expanded,
  onToggleExpand,
  deleteMode,
  selectionState,
  onToggleSelection,
}: {
  group: NameGroup;
  expanded: boolean;
  onToggleExpand: () => void;
  deleteMode: boolean;
  selectionState: GroupSelectionState;
  onToggleSelection: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggleExpand}
      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40"
    >
      {deleteMode && (
        <GroupCheckbox state={selectionState} onClick={onToggleSelection} />
      )}
      <GroupThumbnail ref={group.aggregate.thumbnailRef} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-base font-semibold text-foreground">
            {group.name}
          </span>
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            ×{group.aggregate.totalQty}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {group.aggregate.totalValue > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ${group.aggregate.totalValue.toFixed(2)}
            </span>
          )}
          <StatusPills stats={group.aggregate.statusBreakdown} />
        </div>
      </div>
      <span className="shrink-0 text-muted-foreground">
        {expanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </span>
    </button>
  );
}

function VersionGroupHeader({
  version,
  expanded,
  onToggleExpand,
  deleteMode,
  selectionState,
  onToggleSelection,
}: {
  version: VersionGroup;
  expanded: boolean;
  onToggleExpand: () => void;
  deleteMode: boolean;
  selectionState: GroupSelectionState;
  onToggleSelection: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggleExpand}
      className="flex w-full items-center gap-3 bg-muted/15 px-3 py-2 pl-6 text-left hover:bg-muted/40"
    >
      {deleteMode && (
        <GroupCheckbox state={selectionState} onClick={onToggleSelection} />
      )}
      <GroupThumbnail ref={version.aggregate.thumbnailRef} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {version.label}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium text-muted-foreground">
            ×{version.aggregate.totalQty}
          </span>
          {version.aggregate.totalValue > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ${version.aggregate.totalValue.toFixed(2)}
            </span>
          )}
          <StatusPills stats={version.aggregate.statusBreakdown} />
        </div>
      </div>
      <span className="shrink-0 text-muted-foreground">
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </span>
    </button>
  );
}
